import {
	BadmintonLiveScore,
	BaseballLiveScore,
	BasketballLiveScore,
	BeachVolleyballLiveScore,
	CricketLiveScore,
	FootballLiveScore,
	IceHockeyLiveScore,
	SnookerLiveScore,
	SepakTakrawLiveScore,
	TableTennisLiveScore,
	TennisLiveScore,
	VolleyballLiveScore
} from './scoreboard';

export interface OddsQueryParams {
	sport: number;
	market: string;
	betTypeClass: string;
	gameType?: number;
	matchId?: number;
	leagueGroupIds?: Array<number>;
	/** 
	* Used in market 'e' 
	*/
	dateText?: string;
}

export type SportQueryParams = Omit<OddsQueryParams, 'sport'> & {
	sport: number | number[];
};

/**
 * Parameters Related to OddsDirector functions
 */
export type OddsDirectorParams = Omit<SportQueryParams, 'matchId' | 'dateText'>;

/**
 * Full odds data from odds service
 */
export interface SportOddsData {
	oddsData: OddsModel | null;
	nextTime: number;
	hKey: string;
}

/**
 * Odds model from SportOddsData["oddsData"] (from odds service)
 */
export interface OddsModel {
	sportId: number | number[];
	//market: string;
	MatchCollection: SportMatchModel[];
	teamNameMap: Map<number, string>;
	leagueNameMap: Map<number, string>;
}

/**
 * Original match model (no other properties, e.g. BetTypes)
 */
export interface MatchModelBase {
	BBVD: number;
	CCode: string;
	CO?: number;
	Etm: string;
	FavMarketType: string;
	Fixing1: boolean;
	Fixing2: boolean;
	GameID: number;
	GameTime: string;
	GST: number;
	GV: boolean;
	GvCombo: number;
	hasLive: boolean;
	HasParlay: boolean;
	HLS: number;
	ID34: string;
	IsLive: boolean;
	IsMainMarket: boolean;
	IsTest: boolean;
	Ktm: string;
	LDM: number;
	LeagueGroupId: number;
	LeagueId: number;
	Markets?: Array<Market>;
	MaT: string;
	MatchCode: string;
	MatchId: number;
	MDM: number;
	Moc: number;
	CMT: number;
	CMP: number;
	DPC: number;
	mslt: Record<string, {
		smid: string;
		smsc: number;
		smfx: boolean;
		SiteId: number;
	}> | null;
	Neu: boolean;
	Parentid: number;
	Parentmatchid: number;
	Parentmatchid2?: number;
	Rc1: number;
	Rc2: number;
	ShowTime: string;
	Sid: string;
	Sid2?: string;
	Src: number;
	Src2: number;
	Sup: boolean;
	T1V: number;
	T2V: number;
	TeamId1: number;
	TeamId2: number;
	Tsp: boolean;
	Tsta: boolean;
	TDIF?: number;
	lsmid?: string;
	BOF?: string;
	ISS?: number;
	GTY?: number;
	hasPM?: boolean;
	/** Provider List (for statistic info)
	 * @param key Provider Id (201: Betradar, 25: LSports)
	 */
	PST?: {
		[key: string]: number;
	}
}

export interface CashOutMatchModel {
	CO?: number;
}

export interface SoccerMatchModel extends MatchModelBase, CashOutMatchModel {
	fast?: boolean;
	htm: number;
	MBO: number;
}

export interface BasketballMatchModel extends MatchModelBase, CashOutMatchModel {
	Gss: number;
	ltm?: Array<number>;
	Sest: number;
	ls: BasketballLiveScore;
};

export interface BaseballMatchModel extends MatchModelBase {
	ls: BaseballLiveScore;
};

export interface TennisMatchModel extends MatchModelBase {
	ls: TennisLiveScore;
};

export interface VolleyballMatchModel extends MatchModelBase {
	ls: VolleyballLiveScore;
};

export interface BeachVolleyballMatchModel extends MatchModelBase {
	ls: BeachVolleyballLiveScore;
};

export interface SnookerMatchModel extends MatchModelBase {
	ls: SnookerLiveScore;
};

export interface BadmintonMatchModel extends MatchModelBase {
	ls: BadmintonLiveScore;
};

export interface FootballMatchModel extends MatchModelBase {
	ls: FootballLiveScore;
};

export interface IceHockeyMatchModel extends MatchModelBase {
	ls: IceHockeyLiveScore;
};

export interface TableTennisMatchModel extends MatchModelBase {
	ls: TableTennisLiveScore;
};

export interface SepakTakrawMatchModel extends MatchModelBase {
	ls: SepakTakrawLiveScore;
};

export interface CricketMatchModel extends MatchModelBase {
	HID2: number;
	AID2: number;
	dwmid: string;
	ls: CricketLiveScore;
	Mocfc: number;
};

export type MatchModelUnion = MatchModelBase | SoccerMatchModel | BasketballMatchModel | CricketMatchModel | BeachVolleyballMatchModel;

/**
 * Handled match model for odds model
 */
export interface SportMatchModel extends MatchModelBase {
	BetTypes: Market[];
	MoreBetTypes: Market[];
	SpMatches?: SportMatchModel[];
	teamNameMap: Map<number, string>;
	leagueNameMap: Map<number, string>;
}

export interface Selection {
	Price: number;
	SelId: string;
	Seq: number;
	Info?: string;
}
export interface IndependentSpreadGroup {
	GroupID: number,
	Gap: number
}
/**
 * Odds entity for bet type, aka market
 */
export interface Market {
	BetTypeId: number;
	Cat: number;
	EXSP: number;
	idpdatbe: Record<string, IndependentSpreadGroup[]>;
	IsCP: boolean;
	LEXSP: number;
	Line: number;
	MarketId: number;
	MatchId: number;
	PDM: number;
	Paj: number;
	Pfm: number;
	Pim: boolean;
	Pty: number;
	Resourceid: string;
	SOGP: string;
	SOGP188?: string;
	hfatbe?: number;
	hasParlay: boolean;
	sort: number;
	gt?: string;
	lkdid?: number;
	ospa?: number;
	Selections: Record<string, Selection>;
	plr?: Record<string, string>;
	hdp1?: number;
	hdp2?: number;
}

export interface OutrightMarket extends Market {
	MatchId: number;
	MarketId: number;
	TeamId: number;
	Price: number;
	IsCP: boolean;
}

export type GameType = 'FX' | 'FC' | null | undefined;

export enum StrongTeam {
	None,
	Home,
	Away
};